import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCreationsOpen, setIsCreationsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { 
      name: 'Créations personnalisées', 
      path: '/creations',
      hasSubmenu: true,
      submenu: [
        { name: 'Ebooks', path: '/livres-personnalises' },
        { name: 'Films d\'animation', path: '/films-animation' },
        { name: 'Faire-part et événements', path: '/creations' },
        { name: 'Portraits', path: '/portraits' },
        { name: 'Nouveautés', path: '/nouveautes' },
      ]
    },
    { name: 'Comment ça marche', path: '/fonctionnement' },
    { name: 'Tarifs', path: '/tarifs' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-soft' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/eb276de5dddb9e260b685871ba0dd98c/67925e2f9ab9604353cb595c2b9db3a5.png" 
              alt="Pixel Anim Logo" 
              className="h-16 w-auto"
            />
            <span className="text-2xl font-bold bg-gradient-to-r from-primary-blue via-purple-600 to-primary-coral bg-clip-text text-transparent" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Pixel Anim
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <div 
                key={link.path}
                className="relative"
                onMouseEnter={() => link.hasSubmenu && setIsCreationsOpen(true)}
                onMouseLeave={() => link.hasSubmenu && setIsCreationsOpen(false)}
              >
                <Link
                  to={link.path}
                  className="relative font-quicksand font-medium text-gray-700 hover:text-primary-blue transition-colors duration-300 cursor-pointer group flex items-center gap-1"
                >
                  {link.name}
                  {link.hasSubmenu && (
                    <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${isCreationsOpen ? 'rotate-180' : ''}`}></i>
                  )}
                  <span
                    className={`absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary-blue to-primary-mint transition-all duration-300 group-hover:w-full ${
                      location.pathname === link.path ? 'w-full' : ''
                    }`}
                  ></span>
                </Link>

                {/* Submenu Dropdown */}
                {link.hasSubmenu && (
                  <AnimatePresence>
                    {isCreationsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                      >
                        {link.submenu?.map((sublink) => (
                          <Link
                            key={sublink.path + sublink.name}
                            to={sublink.path}
                            className="block px-6 py-3 font-quicksand text-gray-700 hover:bg-gradient-to-r hover:from-primary-blue/5 hover:to-primary-mint/5 hover:text-primary-blue transition-all duration-300 cursor-pointer"
                          >
                            {sublink.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/contact"
              className="px-6 py-3 bg-gradient-to-r from-primary-blue to-purple-600 text-white font-quicksand font-semibold rounded-full hover:shadow-colored-blue transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <span>Commencer</span>
              <i className="ri-sparkling-fill text-primary-yellow"></i>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-900 cursor-pointer"
            aria-label="Menu"
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4"
          >
            {navLinks.map((link) => (
              <div key={link.path}>
                <Link
                  to={link.path}
                  onClick={() => !link.hasSubmenu && setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between py-3 font-quicksand font-medium text-gray-700 hover:text-primary-blue transition-colors cursor-pointer"
                >
                  {link.name}
                  {link.hasSubmenu && (
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  )}
                </Link>
                {link.hasSubmenu && link.submenu && (
                  <div className="pl-4 space-y-2">
                    {link.submenu.map((sublink) => (
                      <Link
                        key={sublink.path + sublink.name}
                        to={sublink.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block py-2 font-quicksand text-sm text-gray-600 hover:text-primary-blue transition-colors cursor-pointer"
                      >
                        {sublink.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block mt-4 px-6 py-3 bg-gradient-to-r from-primary-blue to-purple-600 text-white font-quicksand font-semibold rounded-full text-center cursor-pointer"
            >
              Commencer
            </Link>
          </motion.div>
        )}
      </div>
    </nav>
  );
}